import { titles } from './session.js';
import { openBackdrop, closeBackdrop } from './ui/modal.js';
import { buildDropdown } from './ui/dropdown.js';
import { escapeHtml, toast } from './util.js';
import { activatePreview, setActivePreviewCanvas } from './preview.js';
import {
  ensureTitle, ensureCreative, addFile, addCanvas, nextCanvasName,
  reflectIntoFile, findFileById, findCreativeById, findTitleById, updateSelectBarSub,
} from './library.js';

const uploadEls = {
  step: document.getElementById('uploadStep'),
  head: document.getElementById('uploadTitleHead'),
  fileName: document.getElementById('uploadFileName'),
  body: document.getElementById('uploadBody'),
  breadcrumb: document.getElementById('uploadBreadcrumb'),
  back: document.getElementById('uploadBack'),
  next: document.getElementById('uploadNext'),
};

export let uploadState = null;

export function openUploadModal(file) {
  uploadState = {
    file,
    titleId: null,           // null | tt_xx | '__new__'
    titleName: null,
    titleConfirmed: false,   // new title: confirmed by "다음" button
    newTitleInput: '',
    creativeId: null,
    creativeName: null,
    creativeConfirmed: false,
    newCreativeInput: '',
    mode: 'new',             // 'new' (새 소분류) | 'reflect' (기존 소분류에 반영)
    targetFileId: null,      // when mode==='reflect': which 소분류 to overwrite
    _newlyRevealed: null,    // 'creative' | null
  };
  uploadEls.fileName.textContent = file.name;
  openBackdrop('uploadModal');
  renderUploadModal();
}

function renderUploadModal() {
  // Snapshot current visibility of each collapse-wrap by key
  const prevState = {};
  uploadEls.body.querySelectorAll('.collapse-wrap[data-key]').forEach(el => {
    prevState[el.dataset.key] = !el.classList.contains('collapsed');
  });

  const s = uploadState;
  // Header step text reflects what user needs to do next
  let stepText;
  if (!s.titleId || (s.titleId === '__new__' && !s.titleConfirmed)) {
    stepText = '타이틀을 선택해주세요';
  } else if (!s.creativeId || (s.creativeId === '__new__' && !s.creativeConfirmed)) {
    stepText = '소재를 선택해주세요';
  } else {
    stepText = '업로드할 준비가 되었습니다';
  }
  uploadEls.step.textContent = `업로드 · ${stepText}`;
  uploadEls.head.textContent = '어디에 업로드할까요?';
  renderUploadBreadcrumb();
  uploadEls.body.innerHTML = '';
  renderUploadStackedPicker();
  uploadEls.back.style.display = 'none';
  updateUploadNext();

  // Sync transitions: replay previous state for newly-rendered DOM, then animate to final
  const newWraps = uploadEls.body.querySelectorAll('.collapse-wrap[data-key]');
  newWraps.forEach(el => {
    const key = el.dataset.key;
    const finalCollapsed = el.classList.contains('collapsed');
    const wasVisible = prevState[key];
    // Only animate if visibility actually changes
    if (wasVisible === undefined || wasVisible === !finalCollapsed) return;

    // Apply previous state instantly (no transition)
    el.classList.add('no-transition');
    if (wasVisible) {
      el.classList.remove('collapsed');
    } else {
      el.classList.add('collapsed');
    }
    // Force reflow so above is committed
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;

    requestAnimationFrame(() => {
      el.classList.remove('no-transition');
      if (finalCollapsed) {
        el.classList.add('collapsed');
      } else {
        el.classList.remove('collapsed');
      }
    });
  });
}

function renderUploadBreadcrumb() {
  const s = uploadState;
  const parts = [];
  if (s.titleId === '__new__') parts.push(s.newTitleInput || '새 타이틀');
  else if (s.titleName) parts.push(s.titleName);
  if (s.creativeId === '__new__') parts.push(s.newCreativeInput || '새 소재');
  else if (s.creativeName) parts.push(s.creativeName);
  if (parts.length === 0) {
    uploadEls.breadcrumb.innerHTML = '<span class="crumb">위치를 선택해주세요</span>';
  } else {
    uploadEls.breadcrumb.innerHTML = parts.map(p =>
      `<span class="crumb">${escapeHtml(p)}</span>`
    ).join('<span class="arrow">→</span>');
  }
}

// ===== Stacked picker for upload — title → creative on one screen =====
function renderUploadStackedPicker() {
  const s = uploadState;
  const wrap = document.createElement('div');
  wrap.className = 'stacked-picker';

  const titleIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>`;
  const creativeIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 3v18"/></svg>`;

  // Whether the title section is "done" (existing selected, or new confirmed)
  const titleDone = s.titleId && s.titleId !== '__new__' || (s.titleId === '__new__' && s.titleConfirmed);

  // ===== Section 1: Title =====
  const titleSection = document.createElement('div');
  titleSection.className = 'stack-section';

  const titleLabel = document.createElement('div');
  titleLabel.className = 'picker-section-label';
  titleLabel.textContent = '타이틀';
  titleSection.appendChild(titleLabel);

  // Mode flags
  const titleIsExisting = s.titleId && s.titleId !== '__new__';
  const titleIsNew = s.titleId === '__new__';

  // Always create all elements; hide via class for smooth transitions
  // When existing is selected → only X clear button (no need for new option)
  // When new is being created → only existing dropdown stays hidden
  const showExistingDropdown = !titleIsNew;
  const showNewTitleButton = !titleIsNew && !titleIsExisting;
  const showNewTitleInput = titleIsNew;
  const showTitleClearBtn = titleIsExisting;

  // Existing dropdown — wrapped in inline-input-row with optional X clear button
  const existingDdWrap = document.createElement('div');
  existingDdWrap.dataset.key = 'title-existing';
  existingDdWrap.className = 'collapse-wrap' + (showExistingDropdown ? '' : ' collapsed');

  const existingDdRow = document.createElement('div');
  existingDdRow.className = 'inline-input-row';

  const ddContainer = document.createElement('div');
  ddContainer.style.flex = '1';
  ddContainer.style.minWidth = '0';
  ddContainer.appendChild(buildDropdown({
    items: titles.map(t => ({
      id: t.id, name: t.name,
      meta: `${t.creatives.length}개 대분류 · ${t.creatives.reduce((sum, c) => sum + (c.files || []).reduce((n, f) => n + (f.canvases || []).length, 0), 0)}개 캔버스`,
      icon: titleIcon,
    })),
    selectedId: titleIsExisting ? s.titleId : null,
    placeholder: '기존 타이틀에서 선택',
    searchPlaceholder: '타이틀 검색',
    triggerIcon: titleIcon,
    autoOpen: false,
    onSelect: (item) => {
      uploadState.titleId = item.id;
      uploadState.titleName = item.name;
      uploadState.titleConfirmed = true;
      uploadState.newTitleInput = '';
      uploadState.creativeId = null;
      uploadState.creativeName = null;
      uploadState.creativeConfirmed = false;
      uploadState.newCreativeInput = '';
      uploadState._newlyRevealed = 'creative';
      renderUploadModal();
    },
  }));
  existingDdRow.appendChild(ddContainer);

  if (showTitleClearBtn) {
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'inline-cancel-btn';
    clearBtn.title = '선택 해제';
    clearBtn.setAttribute('aria-label', '선택 해제');
    clearBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
    clearBtn.addEventListener('click', clearTitleSelection);
    existingDdRow.appendChild(clearBtn);
  }
  existingDdWrap.appendChild(existingDdRow);
  titleSection.appendChild(existingDdWrap);

  // OR divider — between existing and "create new" button
  const showTitleDivider = showExistingDropdown && showNewTitleButton;
  const titleDivider = document.createElement('div');
  titleDivider.dataset.key = 'title-or';
  titleDivider.className = 'or-divider collapse-wrap' + (showTitleDivider ? '' : ' collapsed');
  titleDivider.innerHTML = `<div class="line"></div><div class="word">OR</div><div class="line"></div>`;
  titleSection.appendChild(titleDivider);

  // New title button
  const titleCreateBtnWrap = document.createElement('div');
  titleCreateBtnWrap.dataset.key = 'title-newbtn';
  titleCreateBtnWrap.className = 'collapse-wrap' + (showNewTitleButton ? '' : ' collapsed');
  const titleCreateBtn = document.createElement('button');
  titleCreateBtn.type = 'button';
  titleCreateBtn.className = 'create-btn' + (titleIsNew ? ' active' : '');
  titleCreateBtn.innerHTML = `
    <div class="create-icon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
    </div>
    <span class="create-label">새 타이틀 만들기</span>
  `;
  titleCreateBtn.addEventListener('click', () => {
    uploadState.titleId = '__new__';
    uploadState.titleName = null;
    uploadState.titleConfirmed = false;
    uploadState.creativeId = null;
    uploadState.creativeName = null;
    uploadState.creativeConfirmed = false;
    uploadState.newCreativeInput = '';
    renderUploadModal();
    setTimeout(() => document.getElementById('newTitleInp')?.focus(), 100);
  });
  titleCreateBtnWrap.appendChild(titleCreateBtn);
  titleSection.appendChild(titleCreateBtnWrap);

  // Input + confirm/cancel — always created, collapsed when not new mode
  const titleNewBlock = document.createElement('div');
  titleNewBlock.dataset.key = 'title-newinput';
  titleNewBlock.className = 'new-input-block collapse-wrap' + (showNewTitleInput ? ' show' : ' collapsed');
  titleNewBlock.innerHTML = `
    <div class="inline-input-row">
      <input type="text" id="newTitleInp" placeholder="예: Cat Shinobi" value="${escapeHtml(s.newTitleInput)}" ${s.titleConfirmed ? 'readonly' : ''} />
      ${s.titleConfirmed ? '' : `<button type="button" class="inline-confirm-btn" id="confirmTitleBtn" ${s.newTitleInput.trim().length === 0 ? 'disabled' : ''}>확정</button>`}
      <button type="button" class="inline-cancel-btn" id="cancelTitleBtn" aria-label="취소" title="취소">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    ${s.titleConfirmed ? '' : '<div class="input-help">자유 형식 · 영문/한글/숫자 가능</div>'}
  `;
  titleSection.appendChild(titleNewBlock);

  wrap.appendChild(titleSection);

  // ===== Section 2: Creative — only if title is done =====
  if (titleDone) {
    const creativeSection = document.createElement('div');
    creativeSection.className = 'stack-section' + (s._newlyRevealed === 'creative' ? ' newly-revealed' : '');

    const creativeLabel = document.createElement('div');
    creativeLabel.className = 'picker-section-label';
    creativeLabel.textContent = '소재';
    creativeSection.appendChild(creativeLabel);

    // List existing creatives only if title is existing (not new)
    let creatives = [];
    if (s.titleId !== '__new__') {
      const t = titles.find(t => t.id === s.titleId);
      creatives = t ? t.creatives : [];
    }

    const creativeIsExisting = s.creativeId && s.creativeId !== '__new__';
    const creativeIsNew = s.creativeId === '__new__';
    // New title can't have existing creatives — hide entire existing branch
    const isNewTitleMode = s.titleId === '__new__';
    const showExistingCreativeList = !creativeIsNew && !isNewTitleMode;
    const showNewCreativeButton = !creativeIsNew && !creativeIsExisting;
    const showNewCreativeInput = creativeIsNew;
    const showCreativeClearBtn = creativeIsExisting;

    // Existing creatives dropdown OR empty message
    const existingCWrap = document.createElement('div');
    existingCWrap.dataset.key = 'creative-existing';
    existingCWrap.className = 'collapse-wrap' + (showExistingCreativeList ? '' : ' collapsed');

    if (creatives.length > 0) {
      const cRow = document.createElement('div');
      cRow.className = 'inline-input-row';

      const cDdContainer = document.createElement('div');
      cDdContainer.style.flex = '1';
      cDdContainer.style.minWidth = '0';
      cDdContainer.appendChild(buildDropdown({
        items: creatives.map(c => ({
          id: c.id, name: c.name,
          meta: `${(c.files || []).length}개 소분류`,
          icon: creativeIcon,
        })),
        selectedId: creativeIsExisting ? s.creativeId : null,
        placeholder: '기존 소재에 추가',
        searchPlaceholder: '소재 검색',
        triggerIcon: creativeIcon,
        autoOpen: false,
        onSelect: (item) => {
          uploadState.creativeId = item.id;
          uploadState.creativeName = item.name;
          uploadState.creativeConfirmed = true;
          uploadState.newCreativeInput = '';
          uploadState.mode = 'new';
          uploadState.targetFileId = null;
          renderUploadModal();
        },
      }));
      cRow.appendChild(cDdContainer);

      if (showCreativeClearBtn) {
        const cClearBtn = document.createElement('button');
        cClearBtn.type = 'button';
        cClearBtn.className = 'inline-cancel-btn';
        cClearBtn.title = '선택 해제';
        cClearBtn.setAttribute('aria-label', '선택 해제');
        cClearBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
        cClearBtn.addEventListener('click', clearCreativeSelection);
        cRow.appendChild(cClearBtn);
      }
      existingCWrap.appendChild(cRow);
    } else if (s.titleId !== '__new__') {
      const empty = document.createElement('div');
      empty.className = 'picker-empty';
      empty.textContent = '이 타이틀에는 아직 소재가 없습니다. 아래에서 새로 만들어주세요.';
      empty.style.marginBottom = '12px';
      existingCWrap.appendChild(empty);
    }
    creativeSection.appendChild(existingCWrap);

    // OR divider — only when both branches are visible
    const showCDivider = showExistingCreativeList && showNewCreativeButton && creatives.length > 0;
    const cDivider = document.createElement('div');
    cDivider.dataset.key = 'creative-or';
    cDivider.className = 'or-divider collapse-wrap' + (showCDivider ? '' : ' collapsed');
    cDivider.innerHTML = `<div class="line"></div><div class="word">OR</div><div class="line"></div>`;
    creativeSection.appendChild(cDivider);

    // New creative button
    const creativeCreateBtnWrap = document.createElement('div');
    creativeCreateBtnWrap.dataset.key = 'creative-newbtn';
    creativeCreateBtnWrap.className = 'collapse-wrap' + (showNewCreativeButton ? '' : ' collapsed');
    const creativeCreateBtn = document.createElement('button');
    creativeCreateBtn.type = 'button';
    creativeCreateBtn.className = 'create-btn' + (creativeIsNew ? ' active' : '');
    creativeCreateBtn.innerHTML = `
      <div class="create-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </div>
      <span class="create-label">새 소재 만들기</span>
    `;
    creativeCreateBtn.addEventListener('click', () => {
      uploadState.creativeId = '__new__';
      uploadState.creativeName = null;
      uploadState.creativeConfirmed = false;
      renderUploadModal();
      setTimeout(() => document.getElementById('newCreativeInp')?.focus(), 100);
    });
    creativeCreateBtnWrap.appendChild(creativeCreateBtn);
    creativeSection.appendChild(creativeCreateBtnWrap);

    // New creative input block
    const creativeNewBlock = document.createElement('div');
    creativeNewBlock.dataset.key = 'creative-newinput';
    creativeNewBlock.className = 'new-input-block collapse-wrap' + (showNewCreativeInput ? ' show' : ' collapsed');
    creativeNewBlock.innerHTML = `
      <div class="inline-input-row">
        <input type="text" id="newCreativeInp" placeholder="예: v4 또는 v4_endcard_A" value="${escapeHtml(s.newCreativeInput)}" ${s.creativeConfirmed ? 'readonly' : ''} />
        ${s.creativeConfirmed ? '' : `<button type="button" class="inline-confirm-btn" id="confirmCreativeBtn" ${s.newCreativeInput.trim().length === 0 ? 'disabled' : ''}>확정</button>`}
        <button type="button" class="inline-cancel-btn" id="cancelCreativeBtn" aria-label="취소" title="취소">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      ${s.creativeConfirmed ? '' : '<div class="input-help">버전·variant 식별자 · 영문/숫자/언더스코어 권장</div>'}
    `;
    creativeSection.appendChild(creativeNewBlock);

    wrap.appendChild(creativeSection);

    // ===== Section 3: 반영 방식 — only for an EXISTING creative that already
    // has 소분류 files. New title/creative always create a new 소분류. =====
    const creativeChosen = creativeIsExisting && !isNewTitleMode;
    const targetCreative = creativeChosen
      ? (titles.find(t => t.id === s.titleId)?.creatives.find(c => c.id === s.creativeId) || null)
      : null;
    const existingFiles = targetCreative ? (targetCreative.files || []) : [];
    if (creativeChosen && existingFiles.length) {
      wrap.appendChild(renderReflectSection(existingFiles));
    }
  }

  uploadEls.body.appendChild(wrap);

  // Wire up new title/creative input listeners + confirm buttons
  document.getElementById('newTitleInp')?.addEventListener('input', (e) => {
    uploadState.newTitleInput = e.target.value;
    renderUploadBreadcrumb();
    // Enable/disable confirm button without re-render (less flicker)
    const btn = document.getElementById('confirmTitleBtn');
    if (btn) btn.disabled = e.target.value.trim().length === 0;
    updateUploadNext();
  });
  document.getElementById('newTitleInp')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && uploadState.newTitleInput.trim().length > 0) {
      e.preventDefault();
      confirmNewTitle();
    }
  });
  document.getElementById('confirmTitleBtn')?.addEventListener('click', confirmNewTitle);
  document.getElementById('cancelTitleBtn')?.addEventListener('click', cancelNewTitle);

  document.getElementById('newCreativeInp')?.addEventListener('input', (e) => {
    uploadState.newCreativeInput = e.target.value;
    renderUploadBreadcrumb();
    const btn = document.getElementById('confirmCreativeBtn');
    if (btn) btn.disabled = e.target.value.trim().length === 0;
    updateUploadNext();
  });
  document.getElementById('newCreativeInp')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && uploadState.newCreativeInput.trim().length > 0) {
      e.preventDefault();
      confirmNewCreative();
    }
  });
  document.getElementById('confirmCreativeBtn')?.addEventListener('click', confirmNewCreative);
  document.getElementById('cancelCreativeBtn')?.addEventListener('click', cancelNewCreative);

  // Clear newly-revealed flag after render
  s._newlyRevealed = null;
}

// ===== Section 3: choose how to apply the upload into an existing 대분류 =====
function renderReflectSection(existingFiles) {
  const s = uploadState;
  const fileIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;

  const section = document.createElement('div');
  section.className = 'stack-section newly-revealed';

  const label = document.createElement('div');
  label.className = 'picker-section-label';
  label.textContent = '반영 방식';
  section.appendChild(label);

  // Two-way segmented choice: 새 소분류 vs 기존 소분류에 반영
  const seg = document.createElement('div');
  seg.className = 'reflect-choice';
  seg.innerHTML = `
    <button type="button" class="reflect-opt${s.mode === 'new' ? ' active' : ''}" data-mode="new">
      <span class="reflect-opt-title">새 소분류로 추가</span>
      <span class="reflect-opt-sub">파일명으로 새 항목 생성</span>
    </button>
    <button type="button" class="reflect-opt${s.mode === 'reflect' ? ' active' : ''}" data-mode="reflect">
      <span class="reflect-opt-title">기존 소분류에 반영</span>
      <span class="reflect-opt-sub">base 교체 + 캔버스 다이나믹 갱신</span>
    </button>`;
  seg.querySelectorAll('.reflect-opt').forEach((btn) => {
    btn.addEventListener('click', () => {
      uploadState.mode = btn.dataset.mode;
      if (uploadState.mode === 'new') uploadState.targetFileId = null;
      renderUploadModal();
    });
  });
  section.appendChild(seg);

  // Target 소분류 picker — only when reflecting
  if (s.mode === 'reflect') {
    const pickWrap = document.createElement('div');
    pickWrap.style.marginTop = '12px';
    pickWrap.appendChild(buildDropdown({
      items: existingFiles.map((f) => ({
        id: f.id, name: f.name,
        meta: `${(f.canvases || []).length}개 캔버스`,
        icon: fileIcon,
      })),
      selectedId: s.targetFileId,
      placeholder: '반영할 소분류 선택',
      searchPlaceholder: '소분류 검색',
      triggerIcon: fileIcon,
      onSelect: (item) => {
        uploadState.targetFileId = item.id;
        renderUploadModal();
      },
    }));
    section.appendChild(pickWrap);
  }

  return section;
}

// Confirm new title — reveals creative section, hides existing-title dropdown
function confirmNewTitle() {
  const s = uploadState;
  if (s.newTitleInput.trim().length === 0) return;
  s.titleName = s.newTitleInput.trim();
  s.titleConfirmed = true;
  // Reset downstream
  s.creativeId = null;
  s.creativeName = null;
  s.creativeConfirmed = false;
  s.newCreativeInput = '';
  s._newlyRevealed = 'creative';
  renderUploadModal();
}

// Cancel new title — revert to initial state where both options are visible
function cancelNewTitle() {
  const s = uploadState;
  s.titleId = null;
  s.titleName = null;
  s.titleConfirmed = false;
  s.newTitleInput = '';
  renderUploadModal();
}

// Clear existing title selection — reset to initial state
function clearTitleSelection() {
  const s = uploadState;
  s.titleId = null;
  s.titleName = null;
  s.titleConfirmed = false;
  s.creativeId = null;
  s.creativeName = null;
  s.creativeConfirmed = false;
  s.newCreativeInput = '';
  renderUploadModal();
}

// Confirm new creative — mark as decided so footer "업로드" button activates
function confirmNewCreative() {
  const s = uploadState;
  if (s.newCreativeInput.trim().length === 0) return;
  s.creativeName = s.newCreativeInput.trim();
  s.creativeConfirmed = true;
  renderUploadModal();
}

// Cancel new creative — revert to creative-selection state
function cancelNewCreative() {
  const s = uploadState;
  s.creativeId = null;
  s.creativeName = null;
  s.creativeConfirmed = false;
  s.newCreativeInput = '';
  renderUploadModal();
}

// Clear existing creative selection
function clearCreativeSelection() {
  const s = uploadState;
  s.creativeId = null;
  s.creativeName = null;
  s.creativeConfirmed = false;
  s.newCreativeInput = '';
  renderUploadModal();
}

function updateUploadNext() {
  const s = uploadState;
  // Whether title section is done (existing chosen, or new confirmed)
  const titleDone = (s.titleId && s.titleId !== '__new__') || (s.titleId === '__new__' && s.titleConfirmed);
  // Whether creative section is done (existing chosen, or new confirmed)
  const creativeDone = titleDone && (
    (s.creativeId && s.creativeId !== '__new__') ||
    (s.creativeId === '__new__' && s.creativeConfirmed)
  );

  // When reflecting into an existing 소분류, a target file must also be picked.
  const reflectReady = s.mode !== 'reflect' || !!s.targetFileId;

  if (creativeDone && reflectReady) {
    uploadEls.next.style.display = 'inline-block';
    uploadEls.next.textContent = s.mode === 'reflect' ? '반영' : '업로드';
    uploadEls.next.disabled = false;
  } else {
    uploadEls.next.style.display = 'none';
  }
}

// Footer 'next' button — every upload is now an explicit choice (new 소분류 or
// reflect into a chosen one), so there's no implicit filename conflict to resolve.
uploadEls.next.addEventListener('click', () => {
  finalizeUpload();
});

export async function finalizeUpload() {
  const s = uploadState;
  closeBackdrop('uploadModal');

  const file = s.file;
  const isNewTitle = s.titleId === '__new__';
  const isNewCreative = s.creativeId === '__new__';
  const titleName = s.titleName;
  const creativeName = s.creativeName || '대분류';
  const baseFileName = file.name;

  let source = '';
  try { source = await file.text(); } catch { source = ''; }

  // Show the preview immediately from the local source — no network wait.
  activatePreview({ fileName: file.name, path: `${titleName} / ${creativeName}`, source });

  try {
    // ===== Reflect into an existing 소분류: replace base + reconcile canvases =====
    if (s.mode === 'reflect' && s.targetFileId) {
      const file2 = findFileById(s.targetFileId);
      const creative = file2 ? findCreativeById(file2.creativeId) : null;
      const title = creative ? findTitleById(creative.titleId) : null;
      if (file2 && creative && title) {
        await reflectIntoFile(creative, file2, baseFileName, source);
        updateSelectBarSub();
        const canvas = (file2.canvases || [])[0];
        if (canvas) setActivePreviewCanvas(canvas, title, creative, file2, source);
        toast(`"${file2.name}"에 반영 완료 · 캔버스 ${(file2.canvases || []).length}개 갱신`);
        return;
      }
    }

    // ===== New 소분류 (default): title → 대분류 → new file → first canvas =====
    const titleId = isNewTitle ? null : s.titleId;
    const title = await ensureTitle(titleId, titleName);
    const creativeId = (isNewTitle || isNewCreative) ? null : s.creativeId;
    const creative = await ensureCreative(title, creativeId, creativeName);

    const f = await addFile(creative, baseFileName, source);
    const canvas = await addCanvas({ file: f, pkg: {}, tw: [], name: nextCanvasName(f) });
    updateSelectBarSub();
    setActivePreviewCanvas(canvas, title, creative, f, source);
  } catch (e) {
    console.warn('[upload] save failed:', e.message);
    toast('소재 저장 실패 — 백엔드 연결을 확인하세요');
  }
}
